import { Heading, Icon, Image, Text, TextButton, Upsell } from '@zillow/constellation-10';
import { IconHomesFilled } from '@zillow/constellation-icons';
import { useEffect, useState } from 'react';
import { UpsellTrackingIdType, UpsellType as UpsellTypeApi } from '@zg-rentals/ts-rins-client';
import {
  useUpsellWebApiHandler_clickUpsell,
  useUpsellWebApiHandler_viewUpsell,
} from '@zg-rentals/ts-rins-client-react-query';

import ZillowInsuranceServicesLogo from '../../images/ZillowInsuranceServicesLogo.svg';
import { UpsellDismissedStorageKey } from '../../types';
import { hasUpsellBeenDismissed, useCreateUpsell } from '../../hooks/hooks';
import {
  CLICK_YOUR_RENTAL_APPLICATIONS_UPSELL,
  VIEW_YOUR_RENTAL_APPLICATIONS_UPSELL,
} from '../../utils/ClickstreamEvents';
import type { TrackClickstreamEventFn } from '../../utils/ClickstreamEvents';

type ApplicationUpsellProps = {
  listingAlias: string;
  onCloseUpsell: () => void;
  trackClickstreamEvent: TrackClickstreamEventFn;
};

const TRACKING_ID_TYPE = UpsellTrackingIdType.LISTING_ALIAS;
const UPSELL_TYPE = UpsellTypeApi.YOUR_RENTAL_APPLICATIONS;

const ApplicationUpsell = ({ listingAlias, onCloseUpsell, trackClickstreamEvent }: ApplicationUpsellProps) => {
  const [isUpsellOpen, setIsUpsellOpen] = useState(true);
  const [upsellInfo, setUpsellInfo] = useState({
    shouldDisplayUpsell: false,
    monthlyPriceEstimate: '',
    redirectUrl: '',
    upsellId: '',
    zip: '',
  });

  const { mutate: viewUpsellMutation } = useUpsellWebApiHandler_viewUpsell();
  const { mutate: clickUpsellMutation } = useUpsellWebApiHandler_clickUpsell();

  const { createUpsell: createUpsellMutation, isLoading } = useCreateUpsell({
    onSuccess: (data) => {
      const { id: upsellId, shouldDisplayUpsell, url: redirectUrl } = data.upsell;

      if (shouldDisplayUpsell) {
        setUpsellInfo({
          upsellId,
          redirectUrl,
          ...data.upsell,
        });

        onUpsellView(upsellId);
      }
    },
  });

  useEffect(() => {
    if (!hasUpsellBeenDismissed(UPSELL_TYPE, TRACKING_ID_TYPE, listingAlias)) {
      createUpsellMutation(UPSELL_TYPE, TRACKING_ID_TYPE, listingAlias);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingAlias]);

  const onUpsellView = (upsellId: string) => {
    trackClickstreamEvent(VIEW_YOUR_RENTAL_APPLICATIONS_UPSELL);
    viewUpsellMutation({ postData: { upsellId } });
  };

  const onUpsellClick = () => {
    window.open(upsellInfo.redirectUrl, '_blank');
    trackClickstreamEvent(CLICK_YOUR_RENTAL_APPLICATIONS_UPSELL);
    clickUpsellMutation({
      postData: {
        upsellId: upsellInfo.upsellId,
        sessionGuid: '',
      },
    });
  };

  const onClose = () => {
    localStorage.setItem(`${UpsellDismissedStorageKey.YOUR_RENTAL_APPLICATIONS}${listingAlias}`, JSON.stringify(true));
    setIsUpsellOpen(false);
    onCloseUpsell();
  };

  if (isLoading || !upsellInfo.shouldDisplayUpsell || !isUpsellOpen) {
    return null;
  }

  return (
    <Upsell
      data-testid="ApplicationUpsell"
      header={
        <>
          <Image alt="Zillow Insurance Services Logo" src={ZillowInsuranceServicesLogo} />
          <Heading level={2}>Get a renters insurance quote in minutes</Heading>
        </>
      }
      body={
        <Text>
          Popular plans start at {upsellInfo.monthlyPriceEstimate}/mo near {upsellInfo.zip}.
        </Text>
      }
      media={<Icon render={<IconHomesFilled />} />}
      action={
        <TextButton data-testid="ApplicationUpsell-LearnMoreBtn" onClick={onUpsellClick}>
          Learn more
        </TextButton>
      }
      onOpenChange={(open) => !open && onClose()}
      tone="soft"
      elevated={false}
    />
  );
};

export default ApplicationUpsell;
